import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { 
  Shield, 
  Plus, 
  Edit, 
  Users, 
  Crown,
  Settings,
  Eye,
  FileText,
  Building2,
  Trash2,
  UserPlus
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const AVAILABLE_PERMISSIONS = [
  { 
    id: "create_subsidiary", 
    name: "Create Subsidiaries", 
    description: "Create new subsidiaries and business units",
    category: "Subsidiary Management"
  },
  { 
    id: "manage_subsidiaries", 
    name: "Manage Subsidiaries", 
    description: "Edit and update subsidiary information",
    category: "Subsidiary Management"
  },
  { 
    id: "assign_roles", 
    name: "Assign Roles", 
    description: "Assign roles to other users",
    category: "User Management"
  },
  { 
    id: "view_user_roles", 
    name: "View User Roles", 
    description: "View roles assigned to other users",
    category: "User Management"
  },
  { 
    id: "create_business_plan", 
    name: "Create Business Plans", 
    description: "Create and manage business plans",
    category: "Business Planning"
  },
  { 
    id: "view_executive_dashboard", 
    name: "Executive Dashboard", 
    description: "Access executive-level consolidated reporting",
    category: "Reporting"
  },
  { 
    id: "manage_users", 
    name: "User Management", 
    description: "Full user account management capabilities",
    category: "User Management"
  },
  { 
    id: "view_all_data", 
    name: "View All Data", 
    description: "Access all data across subsidiaries",
    category: "Data Access"
  },
  { 
    id: "export_data", 
    name: "Export Data", 
    description: "Export business plans and reports",
    category: "Data Access"
  },
  { 
    id: "manage_subsidiary_data", 
    name: "Manage Subsidiary Data", 
    description: "Manage data within specific subsidiary",
    category: "Subsidiary Management"
  },
  { 
    id: "view_subsidiary_reports", 
    name: "View Subsidiary Reports", 
    description: "View reports for specific subsidiary",
    category: "Reporting"
  },
  { 
    id: "edit_financial_models", 
    name: "Edit Financial Models", 
    description: "Create and edit financial modeling data",
    category: "Business Planning"
  },
  { 
    id: "view_reports", 
    name: "View Reports", 
    description: "View business reports and analytics",
    category: "Reporting"
  }
];

const RoleManagement = () => {
  const { currentPlan } = useBusinessPlan();
  const [roles, setRoles] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [teamMemberDialogOpen, setTeamMemberDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTab, setActiveTab] = useState("roles");
  const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
  const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
  const [selectedRoleForAssignment, setSelectedRoleForAssignment] = useState(null);
  const [userRoleAssignments, setUserRoleAssignments] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    level: 5,
    permissions: []
  });
  const [memberForm, setMemberForm] = useState({
    full_name: "",
    email: "",
    role_name: "",
    department: ""
  });
  const [assignmentForm, setAssignmentForm] = useState({
    user_id: "",
    role_id: "",
    scope_type: "",
    scope_id: "",
    subsidiary_id: null
  });

  useEffect(() => {
    const initializeData = async () => {
      await fetchRoles();
      if (currentPlan?.id) {
        const members = await fetchTeamMembers();  // Wait for team members first
        await fetchUserRoleAssignments(members);  // Pass team members to filter
      }
    };
    initializeData();
  }, [currentPlan?.id]);

  const fetchRoles = async () => {
    try {
      // Exclude platform roles in Company Settings - only show company/subsidiary roles
      const response = await axios.get(`${API}/roles`, {
        params: {
          exclude_platform: true  // This filters out Super Admin
        }
      });
      setRoles(response.data);
    } catch (error) {
      toast.error("Failed to load roles");
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team-members/list/${currentPlan.id}`);
      const members = response.data?.team_members || [];
      setTeamMembers(members);
      return members;  // Return for chaining
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      return [];
    }
  };

  const fetchUserRoleAssignments = async (teamMembersList = null) => {
    try {
      const response = await axios.get(`${API}/user-roles`);
      const allAssignments = response.data || [];
      
      // Use provided team members list or current state
      const membersToFilter = teamMembersList || teamMembers;
      
      // Filter to only show assignments for team members in current plan
      // IMPORTANT: Use team_member.user_id, not team_member.id
      // user_roles.user_id maps to users table, team_members.user_id also maps to users table
      const teamMemberUserIds = membersToFilter.map(tm => tm.user_id).filter(Boolean);
      const filteredAssignments = allAssignments.filter(assignment => 
        teamMemberUserIds.includes(assignment.user_id)
      );
      
      setUserRoleAssignments(filteredAssignments);
      console.log(`Filtered ${filteredAssignments.length} assignments from ${allAssignments.length} total for ${membersToFilter.length} team members`);
    } catch (error) {
      console.error("Error fetching user role assignments:", error);
      setUserRoleAssignments([]);
    }
  };

  const openAssignRoleDialog = (role) => {
    setSelectedRoleForAssignment(role);
    setAssignmentForm({
      user_id: "",
      role_id: role.id,
      scope_type: role.role_scope,  // Use role's scope (company or subsidiary)
      scope_id: role.role_scope === "subsidiary" ? "" : currentPlan?.company_id || "",  // Will be selected for subsidiary
      subsidiary_id: null  // Keep for backward compatibility
    });
    setAssignRoleDialogOpen(true);
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    
    // Validate scope_id is provided for subsidiary roles
    if (assignmentForm.scope_type === "subsidiary" && !assignmentForm.scope_id) {
      toast.error("Please select a subsidiary");
      return;
    }
    
    try {
      await axios.post(`${API}/user-roles`, assignmentForm);
      toast.success("Role assigned successfully");
      setAssignRoleDialogOpen(false);
      setAssignmentForm({ user_id: "", role_id: "", scope_type: "", scope_id: "", subsidiary_id: null });
      fetchUserRoleAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign role");
    }
  };

  const handleRemoveRoleAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this role assignment?")) return;
    
    try {
      await axios.delete(`${API}/user-roles/${assignmentId}`);
      toast.success("Role assignment removed");
      fetchUserRoleAssignments();
    } catch (error) {
      toast.error("Failed to remove role assignment");
    }
  };

  const getRoleAssignmentCount = (roleId) => {
    return userRoleAssignments.filter(a => a.role_id === roleId).length;
  };

  const getRoleAssignmentsForRole = (roleId) => {
    return userRoleAssignments.filter(a => a.role_id === roleId);
  };

  const getUserAssignedRoles = (userId) => {
    const assignments = userRoleAssignments.filter(a => a.user_id === userId);
    return assignments.map(a => a.role_name).join(", ") || "No roles";
  };

  const handleCreateTeamMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/team-members`, {
        ...memberForm,
        plan_id: currentPlan.id
      });
      toast.success("Team member added successfully");
      setTeamMemberDialogOpen(false);
      resetMemberForm();
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add team member");
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm("Remove this team member?")) return;
    
    try {
      await axios.delete(`${API}/team-members/${memberId}`);
      toast.success("Team member removed");
      fetchTeamMembers();
    } catch (error) {
      toast.error("Failed to remove team member");
    }
  };

  const openEditMemberDialog = (member) => {
    setSelectedMember(member);
    setMemberForm({
      full_name: member.full_name,
      email: member.email,
      role_name: member.role_name,
      department: member.department || ""
    });
    setEditMemberDialogOpen(true);
  };

  const handleUpdateTeamMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/team-members/${selectedMember.id}`, {
        ...memberForm,
        plan_id: currentPlan.id
      });
      toast.success("Team member updated successfully");
      setEditMemberDialogOpen(false);
      setSelectedMember(null);
      resetMemberForm();
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team member");
    }
  };

  const resetMemberForm = () => {
    setMemberForm({
      full_name: "",
      email: "",
      role_name: "",
      department: ""
    });
    setSelectedMember(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/roles`, formData);
      toast.success("Role created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to create role";
      toast.error(errorMessage);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/roles/${selectedRole.id}`, formData);
      toast.success("Role updated successfully");
      setEditDialogOpen(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to update role";
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      level: 5,
      permissions: []
    });
    setSelectedRole(null);
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system_role) {
      toast.error("System roles cannot be deleted");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete the role "${role.name}"?\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      try {
        await axios.delete(`${API}/roles/${role.id}`);
        toast.success("Role deleted successfully");
        fetchRoles();
      } catch (error) {
        const errorMessage = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : "Failed to delete role";
        toast.error(errorMessage);
        console.error("Error deleting role:", error);
      }
    }
  };

  const openEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || "",
      level: role.level,
      permissions: role.permissions || []
    });
    setEditDialogOpen(true);
  };

  const togglePermission = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const getRoleIcon = (role) => {
    if (role.level === 1) return <Crown className="h-4 w-4 text-yellow-600" />;
    if (role.level === 2) return <Shield className="h-4 w-4 text-blue-600" />;
    if (role.level === 3) return <Building2 className="h-4 w-4 text-green-600" />;
    if (role.level === 4) return <FileText className="h-4 w-4 text-purple-600" />;
    return <Eye className="h-4 w-4 text-gray-600" />;
  };

  const getLevelName = (level) => {
    const levels = {
      1: "Executive",
      2: "Administrative", 
      3: "Management",
      4: "Operational",
      5: "Basic"
    };
    return levels[level] || "Custom";
  };

  const groupPermissionsByCategory = () => {
    const grouped = {};
    AVAILABLE_PERMISSIONS.forEach(perm => {
      if (!grouped[perm.category]) {
        grouped[perm.category] = [];
      }
      grouped[perm.category].push(perm);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading roles...</p>
        </div>
      </div>
    );
  }

  const permissionsByCategory = groupPermissionsByCategory();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Role & Team Management</h1>
          <p className="text-gray-600 mt-1">
            Manage roles and team members for your company and subsidiaries
          </p>
          <div className="mt-2 inline-flex items-center px-3 py-1 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
            <Building2 className="h-4 w-4 mr-2" />
            Company Settings - Team Members Only
          </div>
        </div>
      </div>

      {/* Tabs for Roles and Team Members */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="roles">
            <Shield className="h-4 w-4 mr-2" />
            Roles & Permissions
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            Team Members
          </TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-6">
          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <Shield className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900">Company & Subsidiary Roles</h3>
                <p className="text-sm text-amber-700 mt-1">
                  These roles can be assigned to your company team members. Platform-level roles 
                  (like Platform Super Admin) are managed separately in the Admin Dashboard.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Role</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreate} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Role Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter role name"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="level">Hierarchy Level *</Label>
                  <select
                    id="level"
                    value={formData.level}
                    onChange={(e) => setFormData({...formData, level: parseInt(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value={1}>1 - Executive (Highest)</option>
                    <option value={2}>2 - Administrative</option>
                    <option value={3}>3 - Management</option>
                    <option value={4}>4 - Operational</option>
                    <option value={5}>5 - Basic (Lowest)</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe this role's purpose and responsibilities"
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Permissions</Label>
                <div className="mt-2 space-y-4">
                  {Object.entries(permissionsByCategory).map(([category, permissions]) => (
                    <div key={category} className="border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">{category}</h4>
                      <div className="space-y-2">
                        {permissions.map(permission => (
                          <div key={permission.id} className="flex items-start space-x-3">
                            <Checkbox
                              id={permission.id}
                              checked={formData.permissions.includes(permission.id)}
                              onCheckedChange={() => togglePermission(permission.id)}
                            />
                            <div className="flex-1">
                              <label htmlFor={permission.id} className="text-sm font-medium cursor-pointer">
                                {permission.name}
                              </label>
                              <p className="text-xs text-gray-500">{permission.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCreateDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Role
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Roles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <Card key={role.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getRoleIcon(role)}
                  <CardTitle className="text-lg">{role.name}</CardTitle>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">
                    Level {role.level}
                  </Badge>
                  {role.is_system_role && (
                    <Badge variant="secondary" className="text-xs">
                      System
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                {role.description || "No description provided"}
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Hierarchy Level
                    </p>
                    <p className="text-sm font-medium">{getLevelName(role.level)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Scope
                    </p>
                    <Badge 
                      variant={role.role_scope === "company" ? "default" : "outline"}
                      className="text-xs"
                    >
                      {role.role_scope === "company" ? "Company" : "Subsidiary"}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Permissions ({role.permissions?.length || 0})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(role.permissions || []).slice(0, 3).map(permission => {
                      const permDef = AVAILABLE_PERMISSIONS.find(p => p.id === permission);
                      return (
                        <Badge key={permission} variant="outline" className="text-xs">
                          {permDef ? permDef.name : permission}
                        </Badge>
                      );
                    })}
                    {(role.permissions || []).length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{(role.permissions || []).length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="h-4 w-4 mr-1" />
                    {getRoleAssignmentCount(role.id)} user{getRoleAssignmentCount(role.id) !== 1 ? 's' : ''} assigned
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAssignRoleDialog(role)}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Assign
                  </Button>
                </div>
                
                {/* Show assigned users */}
                {getRoleAssignmentsForRole(role.id).length > 0 && (
                  <div className="space-y-1">
                    {getRoleAssignmentsForRole(role.id).slice(0, 2).map(assignment => (
                      <div key={assignment.id} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                        <span className="text-gray-700 truncate">{assignment.user_name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRoleAssignment(assignment.id)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {getRoleAssignmentsForRole(role.id).length > 2 && (
                      <p className="text-xs text-gray-500 pl-2">
                        +{getRoleAssignmentsForRole(role.id).length - 2} more
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-3 flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(role)}
                  disabled={role.is_system_role}
                  title={role.is_system_role ? "System roles cannot be edited" : "Edit role"}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteRole(role)}
                  disabled={role.is_system_role}
                  title={role.is_system_role ? "System roles cannot be deleted" : "Delete role"}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {roles.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Shield className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No roles found</h3>
            <p className="text-gray-500 mb-4">
              Create your first custom role to manage user permissions
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </Button>
          </div>
        )}
      </div>
      </TabsContent>

      {/* Team Members Tab */}
      <TabsContent value="team" className="space-y-6">
        <div className="flex justify-end">
          <Dialog open={teamMemberDialogOpen} onOpenChange={setTeamMemberDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleCreateTeamMember} className="space-y-4">
                <div>
                  <Label htmlFor="member-name">Full Name *</Label>
                  <Input
                    id="member-name"
                    value={memberForm.full_name}
                    onChange={(e) => setMemberForm({...memberForm, full_name: e.target.value})}
                    placeholder="e.g., John Doe"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="member-email">Email *</Label>
                  <Input
                    id="member-email"
                    type="email"
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                    placeholder="e.g., john@company.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="member-role">Role/Title *</Label>
                  <Input
                    id="member-role"
                    value={memberForm.role_name}
                    onChange={(e) => setMemberForm({...memberForm, role_name: e.target.value})}
                    placeholder="e.g., Marketing Manager, CFO, Product Lead"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="member-department">Department (Optional)</Label>
                  <Input
                    id="member-department"
                    value={memberForm.department}
                    onChange={(e) => setMemberForm({...memberForm, department: e.target.value})}
                    placeholder="e.g., Marketing, Finance, Operations"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setTeamMemberDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Add Member
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Team Member Dialog */}
        <Dialog open={editMemberDialogOpen} onOpenChange={(open) => {
          setEditMemberDialogOpen(open);
          if (!open) {
            resetMemberForm();
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleUpdateTeamMember} className="space-y-4">
              <div>
                <Label htmlFor="edit-member-name">Full Name *</Label>
                <Input
                  id="edit-member-name"
                  value={memberForm.full_name}
                  onChange={(e) => setMemberForm({...memberForm, full_name: e.target.value})}
                  placeholder="e.g., John Doe"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-member-email">Email *</Label>
                <Input
                  id="edit-member-email"
                  type="email"
                  value={memberForm.email}
                  onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                  placeholder="e.g., john@company.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-member-role">Role/Title *</Label>
                <Input
                  id="edit-member-role"
                  value={memberForm.role_name}
                  onChange={(e) => setMemberForm({...memberForm, role_name: e.target.value})}
                  placeholder="e.g., Marketing Manager, CFO, Product Lead"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-member-department">Department (Optional)</Label>
                <Input
                  id="edit-member-department"
                  value={memberForm.department}
                  onChange={(e) => setMemberForm({...memberForm, department: e.target.value})}
                  placeholder="e.g., Marketing, Finance, Operations"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  setEditMemberDialogOpen(false);
                  resetMemberForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit">
                  Update Member
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Team Members List */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members ({teamMembers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
                <p className="text-gray-500 mb-4">
                  Add team members to populate owner dropdowns in Strategic Framework
                </p>
                <Button onClick={() => setTeamMemberDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add First Team Member
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Roles</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {teamMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.full_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.role_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.department || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-wrap gap-1">
                            {userRoleAssignments
                              .filter(a => a.user_id === member.id)
                              .map(assignment => (
                                <Badge key={assignment.id} variant="outline" className="text-xs">
                                  {assignment.role_name}
                                </Badge>
                              ))}
                            {userRoleAssignments.filter(a => a.user_id === member.id).length === 0 && (
                              <span className="text-gray-400 text-xs">No roles assigned</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={member.is_active ? "success" : "secondary"}>
                            {member.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditMemberDialog(member)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Edit team member"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTeamMember(member.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete team member"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEdit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Role Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter role name"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-level">Hierarchy Level *</Label>
                <select
                  id="edit-level"
                  value={formData.level}
                  onChange={(e) => setFormData({...formData, level: parseInt(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value={1}>1 - Executive (Highest)</option>
                  <option value={2}>2 - Administrative</option>
                  <option value={3}>3 - Management</option>
                  <option value={4}>4 - Operational</option>
                  <option value={5}>5 - Basic (Lowest)</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe this role's purpose and responsibilities"
                rows={3}
              />
            </div>
            
            <div>
              <Label>Permissions</Label>
              <div className="mt-2 space-y-4">
                {Object.entries(permissionsByCategory).map(([category, permissions]) => (
                  <div key={category} className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">{category}</h4>
                    <div className="space-y-2">
                      {permissions.map(permission => (
                        <div key={permission.id} className="flex items-start space-x-3">
                          <Checkbox
                            id={`edit-${permission.id}`}
                            checked={formData.permissions.includes(permission.id)}
                            onCheckedChange={() => togglePermission(permission.id)}
                          />
                          <div className="flex-1">
                            <label htmlFor={`edit-${permission.id}`} className="text-sm font-medium cursor-pointer">
                              {permission.name}
                            </label>
                            <p className="text-xs text-gray-500">{permission.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                Update Role
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={assignRoleDialogOpen} onOpenChange={setAssignRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Role: {selectedRoleForAssignment?.name}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAssignRole} className="space-y-4">
            <div>
              <Label htmlFor="assign-user">Select Team Member *</Label>
              <select
                id="assign-user"
                value={assignmentForm.user_id}
                onChange={(e) => setAssignmentForm({...assignmentForm, user_id: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Choose a team member...</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.user_id}>
                    {member.full_name} ({member.email})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Only team members from the Team Members tab can be assigned roles
              </p>
            </div>

            {/* Show scope info based on role scope */}
            {selectedRoleForAssignment?.role_scope === "company" && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-900">
                  <strong>Company Role:</strong> This role will apply across all subsidiaries in your company.
                </p>
              </div>
            )}

            {selectedRoleForAssignment?.role_scope === "subsidiary" && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <p className="text-sm text-green-900">
                  <strong>Subsidiary Role:</strong> This role applies to a specific subsidiary. 
                  The subsidiary context will be determined by the team member's assignment.
                </p>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-sm text-gray-700">
                <strong>Permissions:</strong> {selectedRoleForAssignment?.name} role grants {selectedRoleForAssignment?.permissions?.length || 0} permission(s).
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setAssignRoleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                Assign Role
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;